import React from 'react';

interface SkeletonProps {
  className?: string;
}

export const Skeleton: React.FC<SkeletonProps> = ({ className = '' }) => (
  <div className={`animate-pulse bg-gray-200 dark:bg-gray-700/50 rounded-lg ${className}`} />
);

// Matches compact org/admin cards (p-4, rounded-2xl, text-xs/text-sm)
export const SkeletonCard: React.FC = () => (
  <div role="status" aria-label="Loading" className="bg-white dark:bg-gray-800/50 rounded-2xl border border-gray-200 dark:border-transparent p-4">
    <div className="flex items-start gap-2.5 mb-3">
      <Skeleton className="h-8 w-8 rounded-xl flex-shrink-0" />
      <div className="flex-1 min-w-0 space-y-1.5">
        <Skeleton className="h-3.5 w-2/3" />
        <Skeleton className="h-2.5 w-1/3" />
      </div>
      <Skeleton className="h-5 w-12 rounded-full flex-shrink-0" />
    </div>
    <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 mb-3">
      <Skeleton className="h-2.5 w-full" />
      <Skeleton className="h-2.5 w-full" />
      <Skeleton className="h-2.5 w-3/4" />
      <Skeleton className="h-2.5 w-2/3" />
    </div>
    <div className="flex items-center gap-1.5 pt-3 border-t border-gray-100 dark:border-gray-700/50">
      <Skeleton className="h-7 flex-1 rounded-xl" />
      <Skeleton className="h-7 w-7 rounded-lg" />
      <Skeleton className="h-7 w-14 rounded-lg" />
      <Skeleton className="h-7 w-7 rounded-lg" />
    </div>
  </div>
);

// Matches compact table with card view (UserManagement)
export const SkeletonTable: React.FC<{ rows?: number }> = ({ rows = 5 }) => (
  <div role="status" aria-label="Loading" className="bg-white dark:bg-gray-800/50 rounded-2xl border border-gray-200 dark:border-transparent overflow-hidden">
    {/* Header */}
    <div className="bg-gray-50 dark:bg-gray-700/30 px-4 py-2.5 border-b border-gray-200 dark:border-gray-700/50">
      <div className="flex gap-6">
        <Skeleton className="h-3 w-16" />
        <Skeleton className="h-3 w-12" />
        <Skeleton className="h-3 w-12" />
        <Skeleton className="h-3 w-20 hidden sm:block" />
        <Skeleton className="h-3 w-16 hidden md:block" />
      </div>
    </div>
    {/* Rows */}
    <div className="divide-y divide-gray-100 dark:divide-gray-700/50">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="px-4 py-3">
          <div className="flex items-center gap-3">
            <Skeleton className="h-8 w-8 rounded-full flex-shrink-0" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-3 w-32" />
              <Skeleton className="h-2.5 w-44" />
            </div>
            <Skeleton className="h-5 w-14 rounded-full hidden sm:block" />
            <Skeleton className="h-5 w-14 rounded-full hidden sm:block" />
            <Skeleton className="h-6 w-16 rounded-lg" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Matches compact stat cards (p-3.5, rounded-2xl, text-xl values)
export const SkeletonStatCard: React.FC = () => (
  <div role="status" aria-label="Loading" className="bg-white dark:bg-gray-800/50 rounded-2xl border border-gray-200 dark:border-transparent p-3.5">
    <div className="flex items-center justify-between mb-2">
      <Skeleton className="h-8 w-8 rounded-xl" />
      <Skeleton className="h-3 w-10" />
    </div>
    <Skeleton className="h-6 w-16 mb-1" />
    <Skeleton className="h-2.5 w-20" />
  </div>
);

// Matches compact chart areas
export const SkeletonChart: React.FC = () => {
  const barHeights = ['h-16', 'h-24', 'h-20', 'h-28', 'h-22', 'h-26', 'h-18', 'h-30', 'h-20', 'h-24', 'h-22', 'h-26'];
  return (
    <div role="status" aria-label="Loading" className="bg-white dark:bg-gray-800/50 rounded-2xl border border-gray-200 dark:border-transparent p-4">
      <Skeleton className="h-4 w-32 mb-3" />
      <div className="flex items-end gap-1.5 h-36">
        {Array.from({ length: 12 }).map((_, i) => (
          <Skeleton key={i} className={`flex-1 rounded-t-md rounded-b-none ${barHeights[i]}`} />
        ))}
      </div>
    </div>
  );
};

// Matches compact list items
export const SkeletonList: React.FC<{ items?: number }> = ({ items = 3 }) => (
  <div role="status" aria-label="Loading" className="space-y-2">
    {Array.from({ length: items }).map((_, i) => (
      <div key={i} className="bg-white dark:bg-gray-800/50 rounded-2xl border border-gray-200 dark:border-transparent p-3">
        <div className="flex items-center gap-2.5">
          <Skeleton className="h-7 w-7 rounded-lg flex-shrink-0" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-3 w-3/4" />
            <Skeleton className="h-2.5 w-1/2" />
          </div>
        </div>
      </div>
    ))}
  </div>
);
